import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { formatPrice, variantLabel } from "../lib/api";
import { ProductVisual } from "./ProductVisual";

const EmptyDrawer = ({ onClose }) => (
  <div className="flex-1 flex flex-col items-center justify-center gap-4 p-8 text-center">
    <img src="/brand/goat-badge.jpg" alt="" className="h-16 w-16 rounded-full object-cover border border-capra-bronze/40 opacity-70" />
    <p className="font-display text-xl text-capra-ivory">Your cart awaits.</p>
    <p className="text-sm text-capra-muted">The collection is ready when you are.</p>
    <button
      onClick={onClose}
      className="mt-2 px-6 py-3 border border-capra-bronze/50 text-capra-bronze text-[11px] uppercase tracking-[0.22em] hover:bg-capra-bronze hover:text-capra-night transition-colors"
    >
      Explore Collection
    </button>
  </div>
);

const DrawerItem = ({ item, onNavigate, removeItem, updateQuantity }) => (
  <div className="flex gap-4" data-testid={`cart-item-${item.slug}`}>
    <Link to={`/product/${item.slug}`} onClick={onNavigate} className="w-20 h-24 shrink-0 overflow-hidden">
      <ProductVisual product={item} emblemSize="small" />
    </Link>
    <div className="flex-1 min-w-0">
      <div className="flex justify-between gap-2">
        <p className="font-display text-sm text-capra-ivory leading-snug">{item.name}</p>
        <button
          data-testid={`cart-remove-${item.slug}`}
          onClick={() => removeItem(item.key)}
          className="text-capra-muted hover:text-capra-copper transition-colors shrink-0"
          aria-label="Remove item"
        >
          <X size={14} strokeWidth={1.5} />
        </button>
      </div>
      <p className="text-[11px] text-capra-muted mt-1">{variantLabel(item)}</p>
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center border border-white/15">
          <button onClick={() => updateQuantity(item.key, item.quantity - 1)} className="px-2 py-1 text-capra-muted hover:text-capra-ivory" aria-label="Decrease quantity">
            <Minus size={12} />
          </button>
          <span className="px-2 text-xs text-capra-ivory" data-testid={`cart-qty-${item.slug}`}>{item.quantity}</span>
          <button onClick={() => updateQuantity(item.key, item.quantity + 1)} className="px-2 py-1 text-capra-muted hover:text-capra-ivory" aria-label="Increase quantity">
            <Plus size={12} />
          </button>
        </div>
        <p className="text-sm text-capra-gold">{formatPrice(item.price * item.quantity)}</p>
      </div>
    </div>
  </div>
);

const DrawerFooter = ({ subtotal, onCheckout }) => (
  <div className="border-t border-white/10 px-6 py-5 space-y-4 monogram-veil">
    <div className="flex justify-between items-baseline">
      <span className="text-[11px] uppercase tracking-[0.22em] text-capra-muted">Subtotal</span>
      <span className="font-display text-xl text-capra-ivory" data-testid="cart-drawer-subtotal">{formatPrice(subtotal)}</span>
    </div>
    <button
      data-testid="cart-drawer-checkout-button"
      onClick={onCheckout}
      className="w-full py-3.5 bg-capra-bronze text-capra-night text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-capra-amber transition-colors"
    >
      Review &amp; Checkout
    </button>
    <p className="text-center text-[10px] tracking-[0.15em] text-capra-muted/70 uppercase">Complimentary shipping on all orders</p>
  </div>
);

export const CartDrawer = () => {
  const { items, drawerOpen, setDrawerOpen, removeItem, updateQuantity, subtotal } = useCart();
  const navigate = useNavigate();

  const close = () => setDrawerOpen(false);
  const goToCart = () => {
    close();
    navigate("/cart");
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent side="right" aria-describedby={undefined} className="w-full sm:max-w-md bg-capra-night border-l border-capra-bronze/20 flex flex-col p-0" data-testid="cart-drawer">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <SheetTitle className="font-display text-lg tracking-[0.2em] text-capra-ivory uppercase">Your Cart</SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <EmptyDrawer onClose={close} />
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              {items.map((item) => (
                <DrawerItem key={item.key} item={item} onNavigate={close} removeItem={removeItem} updateQuantity={updateQuantity} />
              ))}
            </div>
            <DrawerFooter subtotal={subtotal} onCheckout={goToCart} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
